import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

const Schools = () => {
  const navigate = useNavigate();
  const { isAdmin, isSchoolAdmin, isDistrict, user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    direction: 'desc',
  });
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [districts, setDistricts] = useState([]);
  const { notification, showError, showSuccess, hideNotification } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    district: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    licenseCount: 0,
    adminUserId: '',
  });

  useEffect(() => {
    if (isSchoolAdmin && user?.school?.id) {
      navigate(`/schools/${user.school.id}`, { replace: true });
      return;
    }
    
    fetchSchools();
    if (isAdmin) {
      fetchSchoolAdmins();
      fetchDistricts();
    }
    if (!isAdmin && (isSchoolAdmin || isDistrict)) {
      setSortConfig({
        field: 'licenseCount',
        direction: 'desc',
      });
    }
  }, [isAdmin, isSchoolAdmin, isDistrict, user, navigate]);

  const fetchSchools = async () => {
    try {
      const params = {};
      if (sortConfig.field) {
        params.sortBy = sortConfig.field;
        params.sortOrder = sortConfig.direction;
      }
      const response = await api.get('/admin/schools', { params });
      setSchools(response.data.data.schools || []);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, [sortConfig]);

  const fetchSchoolAdmins = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: { role: 'SCHOOL_ADMIN', status: 'ACTIVE' },
      });
      const adminsWithoutSchool = (response.data.data.users || []).filter(
        (user) => !user.school
      );
      setSchoolAdmins(adminsWithoutSchool);
    } catch (error) {
      console.error('Failed to fetch school admins:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/admin/districts');
      setDistricts(response.data.data.districts || []);
    } catch (error) {
      console.error('Failed to fetch districts:', error);
    }
  };

  const handleCreate = () => {
    setSelectedSchool(null);
    setFormData({
      name: '',
      district: '',
      address: '',
      contactEmail: '',
      contactPhone: '',
      licenseCount: 0,
      adminUserId: '',
    });
    setShowModal(true);
  };

  const handleEdit = (school) => {
    setSelectedSchool(school);
    setFormData({
      name: school.name,
      district: school.district,
      address: school.address || '',
      contactEmail: school.contactEmail,
      contactPhone: school.contactPhone || '',
      licenseCount: school.licenseCount,
      adminUserId: school.adminUserId || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!selectedSchool && !isAdmin) {
        showError('Only administrators can create new schools');
        return;
      }
      
      const submitData = { ...formData };
      if (isSchoolAdmin && selectedSchool) {
        delete submitData.district;
        delete submitData.licenseCount;
      }
      
      if (selectedSchool) {
        await api.patch(`/admin/schools/${selectedSchool.id}`, submitData);
      } else {
        await api.post('/admin/schools', submitData);
      }
      fetchSchools();
      setShowModal(false);
      showSuccess(selectedSchool ? 'School updated successfully' : 'School created successfully');
    } catch (error) {
      console.error('Failed to save school:', error);
      showError(error.response?.data?.message || 'Failed to save school');
    }
  };

  const handleAddLicenses = async (schoolId, additionalLicenses) => {
    try {
      await api.patch(`/admin/schools/${schoolId}/licenses`, {
        additionalLicenses: parseInt(additionalLicenses),
      });
      fetchSchools();
      showSuccess('Licenses added successfully');
    } catch (error) {
      console.error('Failed to add licenses:', error);
      showError(error.response?.data?.message || 'Failed to add licenses');
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading schools...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">School Management</h1>
          <p className="text-gray-600">Manage schools, districts, and license allocation</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
          >
            + Add School
          </button>
        )}
      </div>

      {/* Sort Controls */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          {isAdmin && !isSchoolAdmin && !isDistrict ? (
            <>
              <button
                onClick={() => handleSort('name')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortConfig.field === 'name'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center">
                  Name
                  <SortIcon field="name" />
                </div>
              </button>
              <button
                onClick={() => handleSort('district')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortConfig.field === 'district'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center">
                  District
                  <SortIcon field="district" />
                </div>
              </button>
              <button
                onClick={() => handleSort('licenseCount')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortConfig.field === 'licenseCount'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center">
                  Licenses
                  <SortIcon field="licenseCount" />
                </div>
              </button>
              <button
                onClick={() => handleSort('createdAt')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortConfig.field === 'createdAt'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center">
                  Date Created
                  <SortIcon field="createdAt" />
                </div>
              </button>
            </>
          ) : (
            <button
              onClick={() => handleSort('licenseCount')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                sortConfig.field === 'licenseCount'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center">
                Licenses
                <SortIcon field="licenseCount" />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Schools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {schools.map((school) => (
          <div 
            key={school.id} 
            onClick={() => navigate(`/schools/${school.id}`)}
            className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-800">{school.name}</h3>
              {(isAdmin || isSchoolAdmin) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(school);
                  }}
                  className="text-primary hover:text-primaryDark"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-gray-600">
                <span className="font-medium">District:</span> {school.district}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Email:</span> {school.contactEmail}
              </p>
              {school.contactPhone && (
                <p className="text-gray-600">
                  <span className="font-medium">Phone:</span> {school.contactPhone}
                </p>
              )}
              {!isDistrict && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700">Licenses</span>
                    <span className="text-gray-600">
                      {school.licensesUsed} / {school.licenseCount}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full"
                      style={{
                        width: `${(school.licensesUsed / school.licenseCount) * 100}%`,
                      }}
                    ></div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const additional = prompt('Enter number of licenses to add:');
                        if (additional) {
                          handleAddLicenses(school.id, additional);
                        }
                      }}
                      className="mt-2 text-xs text-primary hover:text-primaryDark"
                    >
                      + Add Licenses
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {schools.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <p className="text-gray-500">No schools found</p>
        </div>
      )}

      {showModal && (isAdmin || (isSchoolAdmin && selectedSchool)) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {selectedSchool ? 'Edit School' : 'Create New School'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    School Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District *
                    </label>
                    <select
                      required
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select a District</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      License Count *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.licenseCount}
                      onChange={(e) =>
                        setFormData({ ...formData, licenseCount: parseInt(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}
                {isAdmin && !selectedSchool && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School Administrator *
                    </label>
                    <select
                      required
                      value={formData.adminUserId}
                      onChange={(e) => setFormData({ ...formData, adminUserId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select a School Administrator</option>
                      {schoolAdmins.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.firstName} {admin.lastName} ({admin.email})
                        </option>
                      ))}
                    </select>
                    {schoolAdmins.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        No available SCHOOL_ADMIN users. Please create a SCHOOL_ADMIN user first.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                >
                  {selectedSchool ? 'Update School' : 'Create School'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default Schools;


